import { useState, useEffect, useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import api from "@/lib/api";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { PublicAmbient, KineticHeading, DonkeyQuote } from "@/components/public/PublicVisuals";
import { Check } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

// Theme fragments fed into the intelligence environment (visual labels, not FAQ copy).
const FRAGMENTS = [
  { label: "COST?", x: 6, y: 9 },
  { label: "REFUNDS?", x: 25, y: 20 },
  { label: "AI?", x: 5, y: 35 },
  { label: "VOICE?", x: 22, y: 51 },
  { label: "WHAT'S FREE?", x: 6, y: 66 },
  { label: "RESULTS?", x: 40, y: 13 },
  { label: "RISK?", x: 24, y: 80 },
];
const HUB = { x: 61, y: 33 };

// ===== Hero: the Donkey as the intelligence core, questions entering the system. =====
function AskDonkeyStage({ activeFragment, pulseKey }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <div ref={ref} className="relative w-full h-[260px] sm:h-[340px] lg:h-[480px] overflow-hidden rounded-3xl border border-white/10 bg-donkey-bg glass" data-testid="ask-donkey-stage">
      {/* blueprint grid */}
      <div aria-hidden className="absolute inset-0 opacity-[0.1]" style={{ backgroundImage: "linear-gradient(rgba(249,115,22,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.3) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
      {/* rim glow behind donkey */}
      <div aria-hidden className="absolute -right-8 bottom-2 w-[78%] h-[82%] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(249,115,22,0.28), transparent 66%)" }} />
      {/* floor glow */}
      <div aria-hidden className="absolute bottom-0 right-0 left-1/3 h-12" style={{ background: "radial-gradient(60% 100% at 72% 100%, rgba(249,115,22,0.32), transparent 70%)" }} />

      {/* label */}
      <span className="absolute top-3 left-4 z-30 text-[10px] font-mono uppercase tracking-[0.22em] text-donkey-orange/80">Ask the Donkey</span>

      {/* connecting data paths (crisp regardless of stretch) */}
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full z-0" aria-hidden>
        {FRAGMENTS.map((fr, i) => (
          <motion.line key={fr.label} x1={fr.x} y1={fr.y} x2={HUB.x} y2={HUB.y}
            stroke={activeFragment === i ? "rgba(249,115,22,0.8)" : "rgba(249,115,22,0.22)"} strokeWidth="1" vectorEffect="non-scaling-stroke"
            initial={reduce ? false : { pathLength: 0, opacity: 0 }}
            animate={inView ? { pathLength: 1, opacity: 1 } : {}}
            transition={{ delay: reduce ? 0 : 0.3 + i * 0.1, duration: 0.6, ease: EASE }} />
        ))}
      </svg>

      {/* intelligence hub node near the Donkey */}
      <span aria-hidden className="absolute z-20 -translate-x-1/2 -translate-y-1/2" style={{ left: `${HUB.x}%`, top: `${HUB.y}%` }}>
        <span className="block w-3 h-3 rounded-full bg-donkey-orange shadow-[0_0_16px_rgba(249,115,22,0.8)]" />
        {!reduce && (
          <motion.span key={`hub-${pulseKey}`} className="absolute inset-0 m-auto w-3 h-3 rounded-full border border-donkey-amber"
            initial={{ scale: 1, opacity: 0.9 }} animate={{ scale: 4, opacity: 0 }} transition={{ duration: 0.8, ease: EASE }} />
        )}
      </span>

      {/* one-time load pulses travelling into the hub */}
      {!reduce && inView && [0, 4].map((idx, k) => (
        <motion.span key={`load-${idx}`} aria-hidden className="absolute z-20 w-1.5 h-1.5 rounded-full bg-donkey-amber shadow-[0_0_10px_rgba(245,166,35,0.9)]"
          initial={{ left: `${FRAGMENTS[idx].x}%`, top: `${FRAGMENTS[idx].y}%`, opacity: 0 }}
          animate={{ left: `${HUB.x}%`, top: `${HUB.y}%`, opacity: [0, 1, 1, 0] }}
          transition={{ delay: 1 + k * 0.5, duration: 0.9, ease: EASE }} />
      ))}

      {/* active-question signal pulse (once per open) */}
      {activeFragment != null && !reduce && (
        <motion.span key={`pulse-${pulseKey}`} aria-hidden className="absolute z-30 w-2 h-2 rounded-full bg-donkey-orange shadow-[0_0_14px_rgba(249,115,22,0.95)]"
          initial={{ left: `${FRAGMENTS[activeFragment].x}%`, top: `${FRAGMENTS[activeFragment].y}%`, opacity: 0 }}
          animate={{ left: `${HUB.x}%`, top: `${HUB.y}%`, opacity: [0, 1, 1, 0] }}
          transition={{ duration: 0.7, ease: EASE }} />
      )}

      {/* question fragments (fewer on mobile) */}
      {FRAGMENTS.map((fr, i) => {
        const active = activeFragment === i;
        return (
          <motion.span key={fr.label}
            className={`absolute z-20 ${i >= 4 ? "hidden sm:inline-flex" : "inline-flex"} items-center rounded-full border px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider backdrop-blur-sm transition-colors duration-300
              ${active ? "border-donkey-orange text-donkey-orange bg-donkey-orange/15 shadow-[0_0_18px_rgba(249,115,22,0.5)]" : "border-white/15 text-donkey-warm/70 bg-white/[0.03]"}`}
            style={{ left: `${fr.x}%`, top: `${fr.y}%` }}
            initial={reduce ? false : { opacity: 0, y: 8, scale: 0.9 }}
            animate={inView ? { opacity: 1, y: 0, scale: active ? 1.08 : 1 } : {}}
            transition={{ delay: reduce ? 0 : 0.35 + i * 0.1, duration: 0.4, ease: EASE }}>
            {fr.label}
          </motion.span>
        );
      })}

      {/* one-time light sweep */}
      {!reduce && inView && (
        <motion.span aria-hidden className="absolute top-0 bottom-0 w-1/4 -skew-x-12 pointer-events-none z-10"
          style={{ background: "linear-gradient(100deg, transparent, rgba(245,166,35,0.22), rgba(255,233,176,0.3), transparent)" }}
          initial={{ left: "-30%" }} animate={{ left: "130%" }} transition={{ delay: 0.6, duration: 1.1, ease: EASE }} />
      )}

      {/* THE DONKEY — high-res, transparent, static & sharp */}
      <img src="/donkey-hero.png" alt="The Honest Donkey"
        className="absolute bottom-0 right-1 lg:right-3 h-[82%] sm:h-[88%] lg:h-[94%] w-auto object-contain object-bottom donkey-alive drop-shadow-[0_0_42px_rgba(249,115,22,0.28)]"
        data-testid="faq-donkey" />
    </div>
  );
}

// Answer body with a one-time "answer found" resolution (mounts fresh on open → plays once).
function AnswerReveal({ text }) {
  const reduce = useReducedMotion();
  return (
    <div>
      <motion.div className="flex items-center gap-2 mb-2.5"
        initial={reduce ? false : { opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, ease: EASE }}>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-donkey-green/50 bg-donkey-green/10 text-donkey-green px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-wider shrink-0">
          <Check className="w-3 h-3" /> Answer found
        </span>
        {!reduce && (
          <motion.span aria-hidden className="h-px flex-1 origin-left bg-gradient-to-r from-donkey-orange to-transparent"
            initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.5, ease: EASE }} />
        )}
      </motion.div>
      <motion.p className="text-donkey-grey leading-relaxed"
        initial={reduce ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1, ease: EASE }}>
        {text}
      </motion.p>
    </div>
  );
}

export default function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [open, setOpen] = useState("");
  const [pulse, setPulse] = useState(0);
  useEffect(() => { api.get("/content").then(({ data }) => setFaqs(data.faqs || [])); }, []);

  const activeIdx = open ? parseInt(open.split("-")[1], 10) : null;
  const activeFragment = activeIdx == null || faqs.length === 0 ? null : activeIdx % FRAGMENTS.length;

  return (
    <div className="relative overflow-hidden">
      <PublicAmbient intensity={0.3} />
      <div className="relative z-10 mx-auto max-w-5xl px-5 sm:px-8 py-16">
        {/* Hero — text left, Donkey intelligence stage right */}
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-10 items-center">
          <div className="order-2 lg:order-1">
            <p className="text-xs uppercase tracking-[0.2em] text-donkey-orange mb-4">FAQ</p>
            <KineticHeading text="Fair questions." className="text-4xl sm:text-5xl" />
            <p className="mt-5 text-donkey-grey">Honest answers, as you'd expect.</p>
            <DonkeyQuote className="mt-5 max-w-lg" quotes={["Ask me anything. I won't sugar-coat it.", "No question is daft. Vague business plans are another matter.", "Straight answers only — that's the whole point."]} />
          </div>
          <div className="order-1 lg:order-2">
            <AskDonkeyStage activeFragment={activeFragment} pulseKey={pulse} />
          </div>
        </div>

        {/* Questions entering the system */}
        <Accordion type="single" collapsible value={open}
          onValueChange={(v) => { setOpen(v); if (v) setPulse((p) => p + 1); }}
          className="mt-12 max-w-3xl mx-auto">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`}
              className="group relative rounded-xl border border-white/10 bg-white/[0.02] mb-3 px-4 overflow-hidden transition-colors duration-300 hover:border-donkey-orange/30 data-[state=open]:border-donkey-orange/45 data-[state=open]:bg-donkey-orange/[0.04] data-[state=open]:shadow-[0_0_30px_rgba(249,115,22,0.12)]">
              {/* active accent bar */}
              <span aria-hidden className="absolute left-0 top-0 h-full w-0.5 bg-donkey-orange opacity-0 group-data-[state=open]:opacity-100 transition-opacity duration-300" />
              {/* hover illumination */}
              <span aria-hidden className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: "radial-gradient(520px circle at 8% 0%, rgba(249,115,22,0.07), transparent 70%)" }} />
              <AccordionTrigger
                className="relative text-donkey-warm hover:text-donkey-orange data-[state=open]:text-donkey-orange text-left transition-colors hover:no-underline"
                data-testid={`faq-${i}`}>
                <span className="flex items-center gap-3 pr-2">
                  <span aria-hidden className="relative shrink-0 w-2 h-2 rounded-full bg-donkey-orange/40 group-data-[state=open]:bg-donkey-orange group-data-[state=open]:shadow-[0_0_10px_rgba(249,115,22,0.8)] transition-all duration-300" />
                  <span>{f.q}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="relative text-donkey-grey leading-relaxed pl-5">
                <AnswerReveal text={f.a} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}
